import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { checkInEvent } from "../api/reservations";
import { useAuth } from "../hooks/useAuth";
import { fetchEventDetail } from "../api/events";
import LoadingSpinner from "../components/LoadingSpinner";
import Footer from "../components/Footer";

const koulen = { fontFamily: "Koulen, sans-serif" };

// Check-in opens this far ahead of the start. Mirrors CHECK_IN_GRACE in
// reservations/models.py; the page only uses it to say when to come back, and
// the server still rules on whether a check-in lands.
const CHECK_IN_GRACE_MS = 60 * 60 * 1000;

const formatWhen = (value) =>
    new Date(value).toLocaleString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });

/**
 * What the volunteer reads, from the server's verdict.
 *
 * Refusals are told apart by `code`, not by the message: this page is the one
 * place that has to turn them into different next steps, and the volunteer
 * who scanned without signing up needs a link, not an explanation.
 */
export function outcomeFor({ error, event }) {
    if (!error) {
        return {
            tone: "ok",
            heading: "You're checked in",
            body: "Your hours start counting once an organizer approves the record.",
        };
    }

    const { status, data } = error.response || {};

    switch (data?.code) {
        case "no_reservation":
            return {
                tone: "action",
                heading: "You're not signed up yet",
                body: "Check-in is for volunteers signed up for this event. Sign up on the event page — an organizer can also add you on the spot.",
            };
        case "already_checked_in":
            return {
                tone: "ok",
                heading: "You're already checked in",
                body: "Nothing more to do. Your attendance is recorded.",
            };
        case "outside_window": {
            // One refusal for "too early" and "too late", and the two want
            // opposite things from the volunteer.
            if (event?.end_date && new Date(event.end_date) < new Date()) {
                return {
                    tone: "warn",
                    heading: "This event has ended",
                    body: "Check-in closed when the event did. Ask an organizer to record your attendance.",
                };
            }
            if (event?.start_date) {
                const opens = new Date(new Date(event.start_date).getTime() - CHECK_IN_GRACE_MS);
                return {
                    tone: "warn",
                    heading: "Check-in isn't open yet",
                    body: `Come back from ${formatWhen(opens)} — check-in opens 1 hour before the event starts.`,
                    retryable: true,
                };
            }
            // Without the event we cannot tell early from late, so claim
            // neither: "come back later" would send someone away from an
            // event that is already over.
            return {
                tone: "warn",
                heading: "Check-in isn't open",
                body: "Check-in runs from 1 hour before the event starts until it ends. Ask an organizer if that doesn't sound right.",
                retryable: true,
            };
        }
        default:
            // A 404 with no code is the event itself, not the reservation --
            // a sheet left up after its event was deleted.
            if (status === 404) {
                return {
                    tone: "warn",
                    heading: "We couldn't find this event",
                    body: "The QR code may be out of date. Ask an organizer which event to check into.",
                };
            }
            return {
                tone: "warn",
                heading: "Check-in didn't go through",
                body: "Something went wrong on our side. Try again, or ask an organizer to record your attendance.",
                retryable: true,
            };
    }
}

const TONE_CLASS = {
    ok: "text-[#1a7f4b]",
    warn: "text-[#b23b3b]",
    action: "text-black",
};

export default function CheckIn() {
    const { pk } = useParams();
    const queryClient = useQueryClient();
    const { user } = useAuth();

    const eventQuery = useQuery({
        queryKey: ["event", pk],
        queryFn: () => fetchEventDetail(pk),
        retry: false,
    });

    // The scan is the instruction -- there is no button to press, which is the
    // whole reason the QR beats the dashboard.
    //
    // A query, even though it POSTs. The obvious shape is a mutation fired
    // from an effect, and it does not survive StrictMode: index.js mounts the
    // app inside it, the effect runs on the first mount, and the result lands
    // on an observer React has already thrown away -- the page sits on its
    // spinner forever, in development only. react-query dedupes a query by
    // key, so both mounts share one request and one verdict. The cache
    // settings then keep a remount, a refocus or a reconnect from posting a
    // second check-in, which would come back "already checked in".
    //
    // The key carries the account as well as the event. Logging out leaves the
    // query cache alone, so on a device that never reloads -- an organizer's
    // tablet, or a session that expires and is signed into by someone else --
    // keying on the event alone would hand the next volunteer the previous
    // one's "You're checked in" without posting anything.
    const checkIn = useQuery({
        queryKey: ["check-in", pk, user?.id],
        queryFn: () => checkInEvent(pk),
        retry: false,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    // Upcoming lists the same reservations, and its own Check in button should
    // be gone by the time the volunteer gets there.
    const checkedIn = checkIn.isSuccess;
    useEffect(() => {
        if (checkedIn) queryClient.invalidateQueries({ queryKey: ["reservations"] });
    }, [checkedIn, queryClient]);

    // Both, not just the check-in: the verdict for "outside the window" reads
    // differently depending on whether the event is still ahead, so rendering
    // before the event has landed can tell a late scanner to come back later.
    if (checkIn.isPending || eventQuery.isPending) return <LoadingSpinner />;

    const event = eventQuery.data;
    const outcome = outcomeFor({ error: checkIn.error, event });
    const showSignUp = checkIn.error?.response?.data?.code === "no_reservation";

    return (
        <div className="bg-white text-left" style={{ fontFamily: "Arial, sans-serif" }}>
            <main className="mx-auto max-w-[640px] px-6 py-[80px] text-center">
                {event?.title && (
                    <p className="text-[20px] leading-[1.3] text-[#5e5e5e]">{event.title}</p>
                )}
                <h1
                    className={`mt-[16px] text-[40px] md:text-[56px] leading-[1.1] tracking-[1.2px] uppercase ${TONE_CLASS[outcome.tone]}`}
                    style={koulen}
                >
                    {outcome.heading}
                </h1>
                <p className="mt-[24px] text-[18px] leading-[1.5] text-[#333]">{outcome.body}</p>

                <div className="mt-[40px] flex flex-col items-center gap-[16px]">
                    {/* The result is cached so a remount cannot post a second
                        check-in, which means a refusal would otherwise stick
                        for the rest of the session -- including the volunteer
                        who scanned early and is now standing there on time. */}
                    {outcome.retryable && (
                        <button
                            type="button"
                            onClick={() => checkIn.refetch()}
                            disabled={checkIn.isFetching}
                            className="inline-block border-2 border-black px-[28px] py-[12px] text-[18px] uppercase tracking-[1px] disabled:opacity-50"
                            style={koulen}
                        >
                            {checkIn.isFetching ? "Trying…" : "Try again"}
                        </button>
                    )}
                    {showSignUp && (
                        <Link
                            to={`/event/detail/${pk}`}
                            className="inline-block border-2 border-black px-[28px] py-[12px] text-[18px] uppercase tracking-[1px]"
                            style={koulen}
                        >
                            Go to the event page
                        </Link>
                    )}
                    <Link to="/mypage/upcoming" className="text-[16px] underline text-[#5e5e5e]">
                        See my upcoming events
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
}
